
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchStockMovementReport,
  exportStockMovementReport,
  clearError,
} from 'store/slices/reportSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { ArrowUpCircle, ArrowDownCircle, Package, Filter, X, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import FormField from '../../components/common/FormField';

const StockMovementReportPage = () => {
  const dispatch = useDispatch();

  const {
    stockMovementReport = {},
    isLoading,
    error,
  } = useSelector((state) => state.report);

  const summary = stockMovementReport?.data?.summary || {};
  const results = stockMovementReport?.data?.results || [];
  const pagination = stockMovementReport?.pagination || {};

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);

  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    period: 'daily',
    purchased_gte: '',
    purchased_lte: '',
    consumed_gte: '',
    consumed_lte: '',
    net_change_gte: '',
    net_change_lte: '',
  });

  const [selectedPeriod, setSelectedPeriod] = useState(null); // For modal
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding default values)
  const hasActiveAdvancedFilters = Object.entries(advancedFilters).some(([key, value]) =>
    key !== 'period' && value !== ''
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'stock_movement',
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.period !== 'daily' && { period: advancedFilters.period }),
      ...(advancedFilters.purchased_gte && { purchased__gte: advancedFilters.purchased_gte }),
      ...(advancedFilters.purchased_lte && { purchased__lte: advancedFilters.purchased_lte }),
      ...(advancedFilters.consumed_gte && { consumed__gte: advancedFilters.consumed_gte }),
      ...(advancedFilters.consumed_lte && { consumed__lte: advancedFilters.consumed_lte }),
      ...(advancedFilters.net_change_gte && { net_change__gte: advancedFilters.net_change_gte }),
      ...(advancedFilters.net_change_lte && { net_change__lte: advancedFilters.net_change_lte }),
    };

    dispatch(fetchStockMovementReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      period: 'daily',
      purchased_gte: '',
      purchased_lte: '',
      consumed_gte: '',
      consumed_lte: '',
      net_change_gte: '',
      net_change_lte: '',
    });
  };

  const openDetailsModal = (row) => {
    setSelectedPeriod(row.original);
    setIsDetailsModalOpen(true);
  };

  const columns = [
    { accessorKey: 'Period', header: 'Date / Period' },
    {
      accessorKey: 'Purchased',
      header: 'Purchased',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <ArrowUpCircle className="h-4 w-4 text-green-600" />
          <span className="font-medium">{row.original.Purchased ?? 0}</span>
        </div>
      ),
    },
    {
      accessorKey: 'Consumed',
      header: 'Consumed',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <ArrowDownCircle className="h-4 w-4 text-red-600" />
          <span className="font-medium">{row.original.Consumed ?? 0}</span>
        </div>
      ),
    },
    {
      accessorKey: 'Net Change',
      header: 'Net Change',
      cell: ({ row }) => {
        const net = row.original['Net Change'];
        const isPositive = net > 0;
        const isNegative = net < 0;
        return (
          <Badge variant={isPositive ? 'default' : isNegative ? 'destructive' : 'secondary'}>
            {isPositive && '+'}{net}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const hasDetails = (row.original.purchase_details?.length > 0 || row.original.consume_details?.length > 0);
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => openDetailsModal(row)}
            disabled={!hasDetails}
            className="flex items-center gap-1"
          >
            <Eye className="h-4 w-4" />
            View Details
          </Button>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Stock Movement Report"
        description="Track purchases, consumption and net stock changes over time"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>Hide <ChevronUp className="h-4 w-4 ml-1" /></>
                ) : (
                  <>Show <ChevronDown className="h-4 w-4 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* <div className="space-y-2">
                <Label htmlFor="period">Period</Label>
                <Select
                  value={advancedFilters.period}
                  onValueChange={(value) => handleAdvancedFilterChange('period', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}
              <FormField
  label="Period"
  name="period"
  type="select"
  value={advancedFilters.period}
  onChange={(e) =>
    handleAdvancedFilterChange('period', e.target.value)
  }
  options={[
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
  ]}
/>


              <div className="space-y-2">
                <Label>Purchased Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.purchased_gte}
                    onChange={(e) => handleAdvancedFilterChange('purchased_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.purchased_lte}
                    onChange={(e) => handleAdvancedFilterChange('purchased_lte', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Consumed Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.consumed_gte}
                    onChange={(e) => handleAdvancedFilterChange('consumed_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.consumed_lte}
                    onChange={(e) => handleAdvancedFilterChange('consumed_lte', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Net Change Range</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.net_change_gte}
                    onChange={(e) => handleAdvancedFilterChange('net_change_gte', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.net_change_lte}
                    onChange={(e) => handleAdvancedFilterChange('net_change_lte', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Period</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.Period || 'All Time'}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Purchased</CardTitle>
            <ArrowUpCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {summary['Total Purchased'] ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Consumed</CardTitle>
            <ArrowDownCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {summary['Total Consumed'] ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Change</CardTitle>
            {(summary['Net Change'] ?? 0) >= 0 ? (
              <ArrowUpCircle className="h-4 w-4 text-green-600" />
            ) : (
              <ArrowDownCircle className="h-4 w-4 text-red-600" />
            )}
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${(summary['Net Change'] ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
            >
              {(summary['Net Change'] ?? 0) >= 0 && '+'}
              {summary['Net Change'] ?? 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading stock movement data...</span>
        </div>
      ) : ( */}
        <DataTable
          data={results}
          columns={columns}
          totalCount={pagination.count}
          searchPlaceholder="Search by date/period..."
          onSearch={setSearchTerm}
          showSearch={false}
          pageIndex={page - 1}
          pageSize={pageSize}
          pageCount={totalPages}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={(newSize) => setPageSize(newSize)}
          isLoading={isLoading}
          onExport={() =>
            dispatch(exportStockMovementReport({
              filters: {
                page,
                page_size: pageSize,
                search: searchTerm,
                report_type: 'stock_movement',
                ...dateFilters,
                ...(advancedFilters.period !== 'daily' && { period: advancedFilters.period }),
                ...(advancedFilters.purchased_gte && { purchased__gte: advancedFilters.purchased_gte }),
                ...(advancedFilters.purchased_lte && { purchased__lte: advancedFilters.purchased_lte }),
                ...(advancedFilters.consumed_gte && { consumed__gte: advancedFilters.consumed_gte }),
                ...(advancedFilters.consumed_lte && { consumed__lte: advancedFilters.consumed_lte }),
                ...(advancedFilters.net_change_gte && { net_change__gte: advancedFilters.net_change_gte }),
                ...(advancedFilters.net_change_lte && { net_change__lte: advancedFilters.net_change_lte }),
              }
            }))
          }
          showExport={true}
          showImport={false}
        />
      {/* )} */}

      {/* DETAILS MODAL */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              Stock Movement Details - {selectedPeriod?.Period || 'N/A'}
            </DialogTitle>
          </DialogHeader>

          {selectedPeriod && (
            <Tabs defaultValue="purchases" className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="purchases">
                  <ArrowUpCircle className="h-4 w-4 mr-2 text-green-600" />
                  Purchases ({selectedPeriod.purchase_details?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="consumption">
                  <ArrowDownCircle className="h-4 w-4 mr-2 text-red-600" />
                  Consumption ({selectedPeriod.consume_details?.length || 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="purchases" className="mt-4">
                {selectedPeriod.purchase_details?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Timestamp</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Sub Category</TableHead>
                        <TableHead>Brand / Model / Serial</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>By</TableHead>
                        <TableHead>Reason / Source</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPeriod.purchase_details.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs">
                            {new Date(item.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell className="font-medium">{item.quantity}</TableCell>
                          <TableCell>{item.sub_category}</TableCell>
                          <TableCell className="text-xs">
                            {item.brand || item.model || item.serial ? (
                              <>
                                {item.brand && <div>Brand: {item.brand}</div>}
                                {item.model && <div>Model: {item.model}</div>}
                                {item.serial && <div>Serial: {item.serial}</div>}
                              </>
                            ) : '-'}
                          </TableCell>
                          <TableCell>₹{item.amount || 0}</TableCell>
                          <TableCell>{item.by}</TableCell>
                          <TableCell className="text-xs">
                            <div>{item.reason || item.source}</div>
                            {item.source_id && <div className="text-muted-foreground">ID: {item.source_id}</div>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No purchase activity on this date
                  </div>
                )}
              </TabsContent>

              <TabsContent value="consumption" className="mt-4">
                {selectedPeriod.consume_details?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Timestamp</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Sub Category</TableHead>
                        <TableHead>To Employee</TableHead>
                        <TableHead>Menu</TableHead>
                        <TableHead>By</TableHead>
                        <TableHead>Source</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPeriod.consume_details.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs">
                            {new Date(item.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell className="font-medium">{item.quantity}</TableCell>
                          <TableCell>{item.sub_category}</TableCell>
                          <TableCell>{item.to_employee || '-'}</TableCell>
                          <TableCell>
                            {item.menu ? (
                              <div>
                                <div>{item.menu}</div>
                                <div className="text-xs text-muted-foreground">
                                  Qty: {item.menu_quantity} × {item.ingredient_quantity_per_menu}
                                </div>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{item.by}</TableCell>
                          <TableCell className="text-xs">{item.source || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No consumption activity on this date
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockMovementReportPage;